import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  BugReport as BugIcon,
  Science as TestTubeIcon,
  Assessment as CoverageIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  LocalOffer as ReleaseIcon,
  Security as SecurityIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckCircleIcon,
  Accessibility as AccessibilityIcon,
  Engineering as EngineeringIcon
} from '@mui/icons-material';
import { get } from '../../services/apiService';
import { useNavigate } from 'react-router-dom';

/**
 * QAHealthDashboard component - Live QE Metrics dashboard
 * @returns {JSX.Element} QAHealthDashboard component
 */
const QAHealthDashboard = () => {
  const navigate = useNavigate();
  
  const [metrics, setMetrics] = useState({
    openProductionIssues: 0,
    automatedTests: 0,
    totalTests: 0,
    coverage: null,
    repositoryCount: 0
  });
  const [releaseData, setReleaseData] = useState({
    lastRelease: null,
    nextRelease: null
  });
  const [snykVulnerabilities, setSnykVulnerabilities] = useState({
    critical: 0,
    high: 0
  });
  const [resolutionTime, setResolutionTime] = useState({
    last7Days: null,
    previous7Days: null,
    percentageChange: null,
    trend: null
  });
  const [storyPoints, setStoryPoints] = useState({
    lastSprint: null,
    previousSprint: null,
    percentageChange: null,
    trend: null
  });
  const [bugsCreated, setBugsCreated] = useState({
    thisWeek: 0,
    thisWeekExtrapolated: 0,
    lastWeek: 0,
    thisMonth: 0,
    thisMonthExtrapolated: 0,
    lastMonth: 0,
    lastMonthExtrapolated: null
  });
  const [bugsClosed, setBugsClosed] = useState({
    thisWeek: 0,
    thisWeekExtrapolated: 0,
    lastWeek: 0,
    thisMonth: 0,
    thisMonthExtrapolated: 0,
    lastMonth: 0,
    lastMonthExtrapolated: null
  });
  const [bugsLoading, setBugsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessibilityWcag, setAccessibilityWcag] = useState({
    '2.0': { active: 0, total: 0 },
    '2.1': { active: 0, total: 0 },
    '2.2': { active: 0, total: 0 },
    overdue: 0,
    byPriority: {},
    loading: true
  });
  const [engineeringProcesses, setEngineeringProcesses] = useState({
    totalRca: 0,
    overdueRca: 0,
    totalTechnicalDebt: 0,
    approvedTechnicalDebt: 0,
    loading: true
  });

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        setError(null);

        // Helper function to normalize repository name for matching (same as RepositoriesView)
        const normalizeRepoName = (repoName) => {
          if (!repoName) return '';
          return repoName.toLowerCase().replace(/^tr\//, '').replace(/\/$/, '');
        };

        // Helper function to check if repository is ignored
        const isRepositoryIgnored = (repo, ignoredRepos) => {
          // First check if repository has ignore flag set
          if (repo.ignore === true) {
            return true;
          }
          
          const repoName = repo.githubRepoName;
          const normalized = normalizeRepoName(repoName);
          
          // Check if repository is in ignored map
          if (ignoredRepos[normalized] || ignoredRepos[repoName]) {
            return true;
          }
          
          // Try with tr/ prefix
          const withPrefix = `tr/${normalized}`;
          if (ignoredRepos[withPrefix]) {
            return true;
          }
          
          return false;
        };

        // Fetch all data in parallel
        const [productionBugsStats, testStats, repositories, streamMappingsResponse, fixVersionsResponse, criticalSnykResponse, highSnykResponse, resolutionTimeResponse, storyPointsResponse, accessibilityStatsResponse, rcaMetricsResponse, technicalDebtMetricsResponse] = await Promise.all([
          get('/api/workitems/production-bugs/public/stats'),
          get('/api/test/public/stats'),
          get('/api/repositories'),
          get('/api/snyk-mapping/repository-streams').catch(() => ({ ignored: {} })), // Fallback if API fails
          get('/api/fix-versions').catch(() => ({ success: false, data: [] })), // Fallback if API fails
          get('/api/snyk/issues?severity=Critical&status=Open&showCurrentDue=true&limit=1').catch(() => ({ pagination: { totalCount: 0 } })), // Critical out of SLA
          get('/api/snyk/issues?severity=High&status=Open&showCurrentDue=true&limit=1').catch(() => ({ pagination: { totalCount: 0 } })), // High out of SLA
          get('/api/workitems/production-bugs/public/resolution-time-last-week').catch(() => ({ success: false, data: null })), // Resolution time data
          get('/api/sprint-report/public/story-points-delivered').catch(() => ({ success: false, data: null })), // Story points delivered
          get('/api/admin/accessibility/stats').catch(() => ({ success: false, stats: { byWcagVersion: {} } })), // Accessibility WCAG stats
          get('/api/engineering-processes/rca/metrics').catch(() => ({ success: false, metrics: { totalItems: 0, overdueItems: 0 } })), // RCA metrics
          get('/api/engineering-processes/technical-debt/metrics').catch(() => ({ success: false, metrics: { totalItems: 0, statusDistribution: [] } })) // Technical Debt metrics
        ]);

        // Calculate open production issues (all bugs that are not closed)
        // Query: workItemType == 'Bug' AND issueSource == 'Production' AND state != 'Closed'
        let openProductionIssues = 0;
        
        console.log('Production bugs stats response:', productionBugsStats);
        
        // Handle different response structures
        const statsData = productionBugsStats?.data || productionBugsStats;
        
        if (statsData) {
          // The API returns stateBreakdown as 'byState' in the response
          // Match Production Defects Overview: only Closed, Done, Removed are "closed"; Resolved is non-closed
          const stateBreakdown = statsData.byState || statsData.stateBreakdown || [];
          const closedStates = ['Closed', 'Done', 'Removed'];
          
          console.log('State breakdown:', stateBreakdown);
          console.log('Total count from API:', statsData.total || statsData.totalCount);
          
          openProductionIssues = stateBreakdown
            .filter(item => {
              const itemState = (item.state || '').trim().toLowerCase();
              const isClosed = closedStates.some(c => itemState.includes(c.toLowerCase()));
              return !isClosed;
            })
            .reduce((sum, item) => sum + (item.count || 0), 0);
          
          console.log(`Final count - Open production issues (all non-closed): ${openProductionIssues}`);
          
          // If we got 0, log all available states for debugging
          if (openProductionIssues === 0 && stateBreakdown.length > 0) {
            console.warn('No bugs found in non-closed states. Available states:');
            stateBreakdown.forEach(s => {
              console.warn(`  - ${s.state}: ${s.count}`);
            });
          }
        } else {
          console.error('No data in production bugs stats response:', productionBugsStats);
        }

        // Get test statistics
        let automatedTests = 0;
        let totalTests = 0;
        if (testStats.success && testStats.data) {
          // Only aggregate Automated-UI, Automated-API, and Automated-EPAM (or EPAM-Automated)
          const breakdown = testStats.data.automatedTests?.breakdown || {};
          automatedTests = (breakdown['Automated-UI'] || 0) + 
                          (breakdown['Automated-API'] || 0) + 
                          (breakdown['Automated-EPAM'] || 0) +
                          (breakdown['EPAM-Automated'] || 0); // Support both naming conventions
          totalTests = testStats.data.totalTests || 0;
        }

        // Get ignored repositories mapping
        const ignoredRepositories = streamMappingsResponse.ignored || {};

        // Calculate average coverage from repositories (excluding ignored ones)
        // Note: /api/repositories returns an array directly, not wrapped in {success, data}
        let averageCoverage = null;
        let repositoryCount = 0;
        const reposArray = Array.isArray(repositories) ? repositories : (repositories.data || []);
        
        if (reposArray.length > 0) {
          // Filter repositories: must have coverage data AND not be ignored
          const reposWithCoverage = reposArray.filter(repo => {
            // Check if coverage exists (not undefined/null) - 0 is a valid coverage value
            const hasCoverage = repo.sonarData && 
                                repo.sonarData.coverage !== undefined && 
                                repo.sonarData.coverage !== null &&
                                !isNaN(parseFloat(repo.sonarData.coverage));
            const notIgnored = !isRepositoryIgnored(repo, ignoredRepositories);
            
            if (hasCoverage && notIgnored) {
              console.log(`Including repo: ${repo.githubRepoName}, coverage: ${repo.sonarData.coverage}%`);
            } else if (!hasCoverage) {
              console.log(`Excluding repo (no coverage): ${repo.githubRepoName}`);
            } else if (!notIgnored) {
              console.log(`Excluding repo (ignored): ${repo.githubRepoName}`);
            }
            
            return hasCoverage && notIgnored;
          });
          
          repositoryCount = reposWithCoverage.length;
          console.log(`Total repositories: ${reposArray.length}, With coverage: ${reposWithCoverage.length}, Ignored: ${Object.keys(ignoredRepositories).length}`);
          
          if (reposWithCoverage.length > 0) {
            const totalCoverage = reposWithCoverage.reduce(
              (sum, repo) => sum + (parseFloat(repo.sonarData.coverage) || 0),
              0
            );
            averageCoverage = Math.round((totalCoverage / reposWithCoverage.length) * 10) / 10; // Round to 1 decimal
            console.log(`Calculated average coverage: ${averageCoverage}% from ${reposWithCoverage.length} repositories`);
          } else {
            console.log('No repositories with coverage data found (excluding ignored)');
          }
        } else {
          console.log('No repositories returned from API');
        }

        // Process release data
        let lastRelease = null;
        let nextRelease = null;
        
        if (fixVersionsResponse && fixVersionsResponse.success && fixVersionsResponse.data) {
          const fixVersions = Array.isArray(fixVersionsResponse.data) ? fixVersionsResponse.data : [];
          const now = new Date();
          
          // Filter releases with releaseDate and sort by date
          const releasesWithDates = fixVersions
            .filter(fv => fv.releaseDate)
            .map(fv => ({
              version: fv.version,
              releaseDate: new Date(fv.releaseDate)
            }))
            .sort((a, b) => b.releaseDate - a.releaseDate); // Sort descending (newest first)
          
          // Find last release (most recent date in the past)
          lastRelease = releasesWithDates.find(r => r.releaseDate <= now) || null;
          
          // Find next release (earliest date in the future)
          const futureReleases = releasesWithDates.filter(r => r.releaseDate > now);
          if (futureReleases.length > 0) {
            // Sort ascending to get the earliest future release
            futureReleases.sort((a, b) => a.releaseDate - b.releaseDate);
            nextRelease = futureReleases[0];
          }
          
          console.log('Release data:', { lastRelease, nextRelease });
        }

        // Process SNYK vulnerability counts (out of SLA)
        let criticalCount = 0;
        let highCount = 0;
        
        if (criticalSnykResponse && criticalSnykResponse.pagination) {
          criticalCount = criticalSnykResponse.pagination.totalCount || 0;
        }
        
        if (highSnykResponse && highSnykResponse.pagination) {
          highCount = highSnykResponse.pagination.totalCount || 0;
        }
        
        console.log('SNYK vulnerabilities out of SLA:', { critical: criticalCount, high: highCount });

        setMetrics({
          openProductionIssues,
          automatedTests,
          totalTests,
          coverage: averageCoverage,
          repositoryCount
        });
        
        setReleaseData({
          lastRelease,
          nextRelease
        });
        
        setSnykVulnerabilities({
          critical: criticalCount,
          high: highCount
        });
        
        // Process resolution time data
        if (resolutionTimeResponse && resolutionTimeResponse.success && resolutionTimeResponse.data) {
          setResolutionTime({
            last7Days: resolutionTimeResponse.data.last7Days?.averageResolutionTime || null,
            previous7Days: resolutionTimeResponse.data.previous7Days?.averageResolutionTime || null,
            percentageChange: resolutionTimeResponse.data.percentageChange || null,
            trend: resolutionTimeResponse.data.trend || null
          });
        }
        
        // Process story points data
        if (storyPointsResponse && storyPointsResponse.success && storyPointsResponse.data) {
          setStoryPoints({
            lastSprint: storyPointsResponse.data.lastSprint?.totalStoryPoints || null,
            previousSprint: storyPointsResponse.data.previousSprint?.totalStoryPoints || null,
            percentageChange: storyPointsResponse.data.percentageChange || null,
            trend: storyPointsResponse.data.trend || null
          });
        }

        // Process accessibility WCAG version data and overdue information
        if (accessibilityStatsResponse && accessibilityStatsResponse.success && accessibilityStatsResponse.stats) {
          const stats = accessibilityStatsResponse.stats;
          const byWcagVersion = stats.byWcagVersion || {};
          const sla = stats.sla || {};
          
          console.log('Accessibility WCAG version data:', byWcagVersion);
          console.log('Accessibility SLA data:', sla);
          
          setAccessibilityWcag({
            '2.0': {
              active: byWcagVersion['2.0']?.active || 0,
              total: byWcagVersion['2.0']?.total || 0
            },
            '2.1': {
              active: byWcagVersion['2.1']?.active || 0,
              total: byWcagVersion['2.1']?.total || 0
            },
            '2.2': {
              active: byWcagVersion['2.2']?.active || 0,
              total: byWcagVersion['2.2']?.total || 0
            },
            overdue: sla.overdue || 0,
            byPriority: sla.breakdown?.overdue || {},
            loading: false
          });
        } else {
          console.log('Accessibility stats response:', accessibilityStatsResponse);
          setAccessibilityWcag({
            '2.0': { active: 0, total: 0 },
            '2.1': { active: 0, total: 0 },
            '2.2': { active: 0, total: 0 },
            overdue: 0,
            byPriority: {},
            loading: false
          });
        }

        // Process engineering processes data
        let totalRca = 0;
        let overdueRca = 0;
        let totalTechnicalDebt = 0;
        let approvedTechnicalDebt = 0;

        if (rcaMetricsResponse && rcaMetricsResponse.success && rcaMetricsResponse.metrics) {
          totalRca = rcaMetricsResponse.metrics.totalItems || 0;
          overdueRca = rcaMetricsResponse.metrics.overdueItems || 0;
        }

        if (technicalDebtMetricsResponse && technicalDebtMetricsResponse.success && technicalDebtMetricsResponse.metrics) {
          totalTechnicalDebt = technicalDebtMetricsResponse.metrics.totalItems || 0;
          // Find approved count from status distribution
          const statusDistribution = technicalDebtMetricsResponse.metrics.statusDistribution || [];
          const approvedStatus = statusDistribution.find(s => 
            s._id && s._id.toLowerCase() === 'approved'
          );
          approvedTechnicalDebt = approvedStatus ? (approvedStatus.count || 0) : 0;
        }

        setEngineeringProcesses({
          totalRca,
          overdueRca,
          totalTechnicalDebt,
          approvedTechnicalDebt,
          loading: false
        });
      } catch (err) {
        console.error('Error fetching health metrics:', err);
        setError('Failed to load health metrics. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    const fetchBugsData = async () => {
      try {
        setBugsLoading(true);
        
        // Helper function to get start of week (Monday)
        const getStartOfWeek = (date) => {
          const d = new Date(date);
          const day = d.getDay();
          const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
          return new Date(d.setDate(diff));
        };
        
        // Helper function to get days in month
        const getDaysInMonth = (date) => {
          return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
        };
        
        // Get current date info
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        
        // Calculate current week range
        const thisWeekStart = getStartOfWeek(now);
        thisWeekStart.setHours(0, 0, 0, 0);
        const thisWeekEnd = new Date(thisWeekStart);
        thisWeekEnd.setDate(thisWeekEnd.getDate() + 6);
        thisWeekEnd.setHours(23, 59, 59, 999);
        
        // Calculate days elapsed in current week (including today)
        const daysElapsedInWeek = Math.floor((now - thisWeekStart) / (1000 * 60 * 60 * 24)) + 1;
        
        // Calculate last week range
        const lastWeekStart = new Date(thisWeekStart);
        lastWeekStart.setDate(lastWeekStart.getDate() - 7);
        const lastWeekEnd = new Date(thisWeekStart);
        lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);
        lastWeekEnd.setHours(23, 59, 59, 999);
        
        // Calculate current month range
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        const thisMonthStart = new Date(currentYear, currentMonth, 1);
        thisMonthStart.setHours(0, 0, 0, 0);
        const thisMonthEnd = new Date(currentYear, currentMonth + 1, 0, 23, 59, 59, 999);
        const daysInCurrentMonth = getDaysInMonth(now);
        const daysElapsedInMonth = now.getDate();
        
        // Calculate last month range
        const lastMonthStart = new Date(currentYear, currentMonth - 1, 1);
        lastMonthStart.setHours(0, 0, 0, 0);
        const lastMonthEnd = new Date(currentYear, currentMonth, 0, 23, 59, 59, 999);
        lastMonthEnd.setHours(23, 59, 59, 999);
        const daysInLastMonth = getDaysInMonth(lastMonthStart);
        
        // Use public stats endpoint which provides monthly data
        // Then calculate weekly data from the trends endpoint or use daily calculations
        const publicStatsResponse = await get('/api/workitems/production-bugs/public/stats');
        const statsData = publicStatsResponse?.data || publicStatsResponse;
        const monthlyData = statsData?.charts?.createdVsResolved || [];
        
        console.log('Monthly data from public stats:', monthlyData);
        console.log('Available months in data:', monthlyData.map(m => m.month));
        
        // Get current and last month names - must match server format
        const getMonthName = (date) => {
          return date.toLocaleString('default', { month: 'short', year: 'numeric' });
        };
        
        const thisMonthName = getMonthName(now);
        const lastMonthName = getMonthName(lastMonthStart);
        
        console.log(`Looking for current month: "${thisMonthName}"`);
        console.log(`Looking for last month: "${lastMonthName}"`);
        
        // Find current month and last month data
        let thisMonthData = monthlyData.find(m => m.month === thisMonthName);
        let lastMonthData = monthlyData.find(m => m.month === lastMonthName);
        
        // If not found, try alternative formats
        if (!thisMonthData) {
          // Try with different locale
          const altName = now.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
          thisMonthData = monthlyData.find(m => m.month === altName);
          console.log(`Trying alternative format "${altName}":`, thisMonthData ? 'found' : 'not found');
        }
        
        if (!lastMonthData) {
          const altName = lastMonthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
          lastMonthData = monthlyData.find(m => m.month === altName);
        }
        
        // Default to 0 if still not found
        thisMonthData = thisMonthData || { created: 0, resolved: 0 };
        lastMonthData = lastMonthData || { created: 0, resolved: 0 };
        
        console.log(`Current month (${thisMonthName}): created=${thisMonthData.created}, resolved=${thisMonthData.resolved}`);
        console.log(`Last month (${lastMonthName}): created=${lastMonthData.created}, resolved=${lastMonthData.resolved}`);
        
        // For weekly data, we need to use the trends endpoint which has daily breakdown
        // But since it requires auth, we'll calculate from monthly data proportionally
        // Get the daily-closed endpoint if available (it's public based on the route)
        let thisWeekClosedCount = 0;
        let lastWeekClosedCount = 0;
        let thisWeekCreatedCount = 0;
        let lastWeekCreatedCount = 0;
        
        try {
          // Try to get daily closed data (check if it's public)
          const dailyClosedResponse = await get('/api/workitems/production-bugs/daily-closed').catch(() => null);
          if (dailyClosedResponse && dailyClosedResponse.success && dailyClosedResponse.data) {
            const dailyData = dailyClosedResponse.data;
            
            // Count current week
            thisWeekClosedCount = dailyData
              .filter(day => {
                const dayDate = new Date(day.date);
                dayDate.setHours(0, 0, 0, 0);
                return dayDate >= thisWeekStart && dayDate <= now;
              })
              .reduce((sum, day) => sum + (day.count || 0), 0);
            
            // Count last week
            lastWeekClosedCount = dailyData
              .filter(day => {
                const dayDate = new Date(day.date);
                dayDate.setHours(0, 0, 0, 0);
                return dayDate >= lastWeekStart && dayDate <= lastWeekEnd;
              })
              .reduce((sum, day) => sum + (day.count || 0), 0);
            
            console.log(`From daily-closed: thisWeek=${thisWeekClosedCount}, lastWeek=${lastWeekClosedCount}`);
          }
        } catch (dailyError) {
          console.warn('Could not fetch daily closed data, will estimate from monthly:', dailyError);
        }
        
        // If daily data not available, estimate weekly from monthly proportionally
        if (thisWeekClosedCount === 0 && thisMonthData.resolved > 0) {
          // Estimate: (monthly resolved / days in month) * days elapsed in week
          const avgDailyClosed = thisMonthData.resolved / daysElapsedInMonth;
          thisWeekClosedCount = Math.round(avgDailyClosed * daysElapsedInWeek);
          console.log(`Estimated thisWeek closed from monthly: ${thisWeekClosedCount}`);
        }
        
        if (lastWeekClosedCount === 0 && lastMonthData.resolved > 0) {
          // Estimate: (last month resolved / days in last month) * 7
          const avgDailyClosedLastMonth = lastMonthData.resolved / daysInLastMonth;
          lastWeekClosedCount = Math.round(avgDailyClosedLastMonth * 7);
          console.log(`Estimated lastWeek closed from monthly: ${lastWeekClosedCount}`);
        }
        
        // For created bugs, estimate similarly
        if (thisMonthData.created > 0) {
          const avgDailyCreated = thisMonthData.created / daysElapsedInMonth;
          thisWeekCreatedCount = Math.round(avgDailyCreated * daysElapsedInWeek);
        }
        
        if (lastMonthData.created > 0) {
          const avgDailyCreatedLastMonth = lastMonthData.created / daysInLastMonth;
          lastWeekCreatedCount = Math.round(avgDailyCreatedLastMonth * 7);
        }
        
        // Use monthly data for current and last month counts
        const thisMonthClosedCount = thisMonthData.resolved || 0;
        const lastMonthClosedCount = lastMonthData.resolved || 0;
        const thisMonthCreatedCount = thisMonthData.created || 0;
        const lastMonthCreatedCount = lastMonthData.created || 0;
        
        console.log(`This month closed: ${thisMonthClosedCount}, created: ${thisMonthCreatedCount}`);
        console.log(`Last month closed: ${lastMonthClosedCount}, created: ${lastMonthCreatedCount}`);
        
        // Calculate extrapolated values for Bugs Closed
        // Current week extrapolated: (bugs closed so far / days elapsed) * 7
        const thisWeekExtrapolated = daysElapsedInWeek > 0 
          ? Math.round((thisWeekClosedCount / daysElapsedInWeek) * 7)
          : 0;
        
        // Current month extrapolated: (bugs closed so far / days elapsed) * days in month
        const thisMonthExtrapolated = daysElapsedInMonth > 0
          ? Math.round((thisMonthClosedCount / daysElapsedInMonth) * daysInCurrentMonth)
          : 0;
        
        // Last month extrapolated to current month's length: (last month bugs / days in last month) * days in current month
        const lastMonthExtrapolated = lastMonthClosedCount > 0 && daysInLastMonth > 0
          ? Math.round((lastMonthClosedCount / daysInLastMonth) * daysInCurrentMonth)
          : null;
        
        // Calculate extrapolated values for Bugs Created
        // Current week extrapolated: (bugs created so far / days elapsed) * 7
        const thisWeekCreatedExtrapolated = daysElapsedInWeek > 0 && thisWeekCreatedCount > 0
          ? Math.round((thisWeekCreatedCount / daysElapsedInWeek) * 7)
          : (thisMonthCreatedCount > 0 ? Math.round((thisMonthCreatedCount / daysElapsedInMonth) * 7) : 0);
        
        // Current month extrapolated: (bugs created so far / days elapsed) * days in month
        const thisMonthCreatedExtrapolated = daysElapsedInMonth > 0
          ? Math.round((thisMonthCreatedCount / daysElapsedInMonth) * daysInCurrentMonth)
          : 0;
        
        // Last month extrapolated to current month's length: (last month bugs / days in last month) * days in current month
        const lastMonthCreatedExtrapolated = lastMonthCreatedCount > 0 && daysInLastMonth > 0
          ? Math.round((lastMonthCreatedCount / daysInLastMonth) * daysInCurrentMonth)
          : null;
        
        console.log(`Weekly estimates - Created: thisWeek=${thisWeekCreatedCount} (extrapolated=${thisWeekCreatedExtrapolated}), lastWeek=${lastWeekCreatedCount}`);
        console.log(`Monthly - Created: thisMonth=${thisMonthCreatedCount} (extrapolated=${thisMonthCreatedExtrapolated}), lastMonth=${lastMonthCreatedCount} (extrapolated=${lastMonthCreatedExtrapolated})`);
        
        // Set Bugs Created with new weekly and monthly calculations
        setBugsCreated({
          thisWeek: thisWeekCreatedCount,
          thisWeekExtrapolated: thisWeekCreatedExtrapolated,
          lastWeek: lastWeekCreatedCount,
          thisMonth: thisMonthCreatedCount,
          thisMonthExtrapolated: thisMonthCreatedExtrapolated,
          lastMonth: lastMonthCreatedCount,
          lastMonthExtrapolated: lastMonthCreatedExtrapolated
        });
        
        // Set Bugs Closed with new weekly and monthly calculations
        setBugsClosed({
          thisWeek: thisWeekClosedCount,
          thisWeekExtrapolated: thisWeekExtrapolated,
          lastWeek: lastWeekClosedCount,
          thisMonth: thisMonthClosedCount,
          thisMonthExtrapolated: thisMonthExtrapolated,
          lastMonth: lastMonthClosedCount,
          lastMonthExtrapolated: lastMonthExtrapolated
        });
      } catch (err) {
        console.error('Error fetching bugs data:', err);
        // Set defaults on error
        setBugsCreated({
          thisWeek: 0,
          thisWeekExtrapolated: 0,
          lastWeek: 0,
          thisMonth: 0,
          thisMonthExtrapolated: 0,
          lastMonth: 0,
          lastMonthExtrapolated: null
        });
        setBugsClosed({
          thisWeek: 0,
          thisWeekExtrapolated: 0,
          lastWeek: 0,
          thisMonth: 0,
          thisMonthExtrapolated: 0,
          lastMonth: 0,
          lastMonthExtrapolated: null
        });
      } finally {
        setBugsLoading(false);
      }
    };

    fetchMetrics();
    fetchBugsData();
  }, []);

  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          backgroundColor: 'background.default',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading health metrics...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          backgroundColor: 'background.default',
          p: 3
        }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        backgroundColor: 'background.default',
        p: 3
      }}
    >
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 600 }}>
        HighQ Health Dashboard
      </Typography>

      <Grid container spacing={3}>
        {/* OPEN PRODUCTION ISSUES Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            onClick={() => {
              window.location.href = '/?section=production-defects';
            }}
            sx={{
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              color: 'white',
              boxShadow: 3,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  <Typography
                    variant="overline"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      opacity: 0.9,
                      display: 'block',
                      mb: 1
                    }}
                  >
                    Open Production Issues
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      mb: 1
                    }}
                  >
                    {metrics.openProductionIssues}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: '1rem'
                    }}
                  >
                    All non-closed bugs
                  </Typography>
                  <Box display="flex" alignItems="center" mt={2}>
                    <TrendingUpIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '1rem' }}>
                      Normal
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    right: -20,
                    top: -20,
                    opacity: 0.2,
                    zIndex: 0
                  }}
                >
                  <BugIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Bugs Created This Month Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              color: 'white',
              boxShadow: 3,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  opacity: 0.9,
                  display: 'block',
                  mb: 1
                }}
              >
                Bugs Created This Month
              </Typography>
              
              {bugsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <CircularProgress size={40} sx={{ color: 'white' }} />
                </Box>
              ) : (
                <>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      mb: 1
                    }}
                  >
                    {bugsCreated.thisMonth}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: '1rem',
                      mb: 1
                    }}
                  >
                    This month
                  </Typography>
                  
                  {bugsCreated.lastWeek > 0 && (
                    <Box display="flex" alignItems="center" mt={2}>
                      {bugsCreated.thisWeekExtrapolated >= bugsCreated.lastWeek ? (
                        <TrendingUpIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8 }} />
                      ) : (
                        <TrendingDownIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8 }} />
                      )}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          opacity: 0.9,
                          fontWeight: 600,
                          fontSize: '1rem'
                        }}
                      >
                        {bugsCreated.thisWeekExtrapolated >= bugsCreated.lastWeek ? '+' : ''}
                        {bugsCreated.lastWeek > 0 
                          ? Math.round(((bugsCreated.thisWeekExtrapolated - bugsCreated.lastWeek) / bugsCreated.lastWeek) * 100)
                          : 0}% vs last week
                      </Typography>
                    </Box>
                  )}
                  
                  {bugsCreated.lastMonthExtrapolated !== null && bugsCreated.lastMonthExtrapolated > 0 && (
                    <Box display="flex" alignItems="center" mt={1}>
                      {bugsCreated.thisMonthExtrapolated >= bugsCreated.lastMonthExtrapolated ? (
                        <TrendingUpIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8 }} />
                      ) : (
                        <TrendingDownIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8 }} />
                      )}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          opacity: 0.9,
                          fontWeight: 600,
                          fontSize: '1rem'
                        }}
                      >
                        {bugsCreated.thisMonthExtrapolated >= bugsCreated.lastMonthExtrapolated ? '+' : ''}
                        {bugsCreated.lastMonthExtrapolated > 0 
                          ? Math.round(((bugsCreated.thisMonthExtrapolated - bugsCreated.lastMonthExtrapolated) / bugsCreated.lastMonthExtrapolated) * 100)
                          : 0}% vs last month
                      </Typography>
                    </Box>
                  )}
                </>
              )}
              
              <Box
                sx={{
                  position: 'absolute',
                  right: -20,
                  top: -20,
                  opacity: 0.2,
                  zIndex: 0
                }}
              >
                <BugIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Bugs Closed This Month Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
              color: 'white',
              boxShadow: 3,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  opacity: 0.9,
                  display: 'block',
                  mb: 1
                }}
              >
                Bugs Closed This Month
              </Typography>
              
              {bugsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <CircularProgress size={40} sx={{ color: 'white' }} />
                </Box>
              ) : (
                <>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      mb: 1
                    }}
                  >
                    {bugsClosed.thisMonth}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: '1rem',
                      mb: 1
                    }}
                  >
                    This month
                  </Typography>
                  
                  {bugsClosed.lastWeek > 0 && (
                    <Box display="flex" alignItems="center" mt={2}>
                      {bugsClosed.thisWeekExtrapolated >= bugsClosed.lastWeek ? (
                        <TrendingUpIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8 }} />
                      ) : (
                        <TrendingDownIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8 }} />
                      )}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          opacity: 0.9,
                          fontWeight: 600,
                          fontSize: '1rem'
                        }}
                      >
                        {bugsClosed.thisWeekExtrapolated >= bugsClosed.lastWeek ? '+' : ''}
                        {bugsClosed.lastWeek > 0 
                          ? Math.round(((bugsClosed.thisWeekExtrapolated - bugsClosed.lastWeek) / bugsClosed.lastWeek) * 100)
                          : 0}% vs last week
                      </Typography>
                    </Box>
                  )}
                  
                  {bugsClosed.lastMonthExtrapolated !== null && bugsClosed.lastMonthExtrapolated > 0 && (
                    <Box display="flex" alignItems="center" mt={1}>
                      {bugsClosed.thisMonthExtrapolated >= bugsClosed.lastMonthExtrapolated ? (
                        <TrendingUpIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8 }} />
                      ) : (
                        <TrendingDownIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8 }} />
                      )}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          opacity: 0.9,
                          fontWeight: 600,
                          fontSize: '1rem'
                        }}
                      >
                        {bugsClosed.thisMonthExtrapolated >= bugsClosed.lastMonthExtrapolated ? '+' : ''}
                        {bugsClosed.lastMonthExtrapolated > 0 
                          ? Math.round(((bugsClosed.thisMonthExtrapolated - bugsClosed.lastMonthExtrapolated) / bugsClosed.lastMonthExtrapolated) * 100)
                          : 0}% vs last month
                      </Typography>
                    </Box>
                  )}
                </>
              )}
              
              <Box
                sx={{
                  position: 'absolute',
                  right: -20,
                  top: -20,
                  opacity: 0.2,
                  zIndex: 0
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* AVERAGE RESOLUTION TIME Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            onClick={() => {
              window.location.href = '/?section=production-defects';
            }}
            sx={{
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #00bcd4 0%, #0097a7 100%)',
              color: 'white',
              boxShadow: 3,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  opacity: 0.9,
                  display: 'block',
                  mb: 1
                }}
              >
                Avg Resolution Time
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  mb: 1
                }}
              >
                {resolutionTime.last7Days !== null ? `${resolutionTime.last7Days} days` : 'N/A'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  opacity: 0.9,
                  fontSize: '1rem',
                  mb: 1
                }}
              >
                Last 7 days
              </Typography>
              {resolutionTime.trend !== null && resolutionTime.percentageChange !== null && (
                <Box display="flex" alignItems="center" mt={2}>
                  {resolutionTime.trend === 'down' ? (
                    <TrendingDownIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8, color: 'rgba(0, 0, 0, 0.7)' }} />
                  ) : (
                    <TrendingUpIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8, color: '#f44336' }} />
                  )}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      opacity: 0.9,
                      color: resolutionTime.trend === 'down' ? 'rgba(0, 0, 0, 0.7)' : '#f44336',
                      fontWeight: 600,
                      fontSize: '1rem'
                    }}
                  >
                    {resolutionTime.trend === 'down' ? '-' : '+'}{Math.abs(resolutionTime.percentageChange)}% vs previous week
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  position: 'absolute',
                  right: -20,
                  top: -20,
                  opacity: 0.2,
                  zIndex: 0
                }}
              >
                <TimeIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* RELEASE Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            onClick={() => {
              window.location.href = '/?section=releases';
            }}
            sx={{
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
              color: 'white',
              boxShadow: 3,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  opacity: 0.9,
                  display: 'block',
                  mb: 2
                }}
              >
                Release
              </Typography>
              
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                {/* Last Release - Left Aligned */}
                <Box flex={1} textAlign="left">
                  {releaseData.lastRelease ? (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          opacity: 0.9,
                          fontSize: '0.85rem',
                          mb: 0.5
                        }}
                      >
                        Completed
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          mb: 0.5
                        }}
                      >
                        {releaseData.lastRelease.version}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          opacity: 0.8,
                          fontSize: '0.8rem'
                        }}
                      >
                        {new Date(releaseData.lastRelease.releaseDate).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </Typography>
                    </>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        opacity: 0.7,
                        fontSize: '0.75rem'
                      }}
                    >
                      No completed release
                    </Typography>
                  )}
                </Box>
                
                {/* Next Release - Right Aligned */}
                <Box flex={1} textAlign="right">
                  {releaseData.nextRelease ? (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          opacity: 0.9,
                          fontSize: '0.85rem',
                          mb: 0.5
                        }}
                      >
                        Upcoming
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          mb: 0.5
                        }}
                      >
                        {releaseData.nextRelease.version}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          opacity: 0.8,
                          fontSize: '0.8rem'
                        }}
                      >
                        {new Date(releaseData.nextRelease.releaseDate).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </Typography>
                    </>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        opacity: 0.7,
                        fontSize: '0.75rem'
                      }}
                    >
                      No upcoming release
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  right: -20,
                  top: -20,
                  opacity: 0.2,
                  zIndex: 0
                }}
              >
                <ReleaseIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* STORY POINTS DELIVERED Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            onClick={() => {
              window.location.href = '/?section=sprint-reports';
            }}
            sx={{
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
              color: 'white',
              boxShadow: 3,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  opacity: 0.9,
                  display: 'block',
                  mb: 1
                }}
              >
                Story Points Delivered
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  mb: 1
                }}
              >
                {storyPoints.lastSprint !== null ? storyPoints.lastSprint : 'N/A'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  opacity: 0.9,
                  fontSize: '1rem',
                  mb: 1
                }}
              >
                Last completed sprint
              </Typography>
              {storyPoints.trend !== null && storyPoints.percentageChange !== null && (
                <Box display="flex" alignItems="center" mt={2}>
                  {storyPoints.trend === 'up' ? (
                    <TrendingUpIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8, color: 'rgba(255, 255, 255, 0.9)' }} />
                  ) : (
                    <TrendingDownIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8, color: 'rgba(255, 255, 255, 0.9)' }} />
                  )}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      opacity: 0.9,
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: 600,
                      fontSize: '1rem'
                    }}
                  >
                    {storyPoints.trend === 'up' ? '+' : ''}{storyPoints.percentageChange}% vs previous sprint
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  position: 'absolute',
                  right: -20,
                  top: -20,
                  opacity: 0.2,
                  zIndex: 0
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* COVERAGE Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            onClick={() => {
              window.location.href = '/?section=repositories';
            }}
            sx={{
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              color: 'white',
              boxShadow: 3,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  <Typography
                    variant="overline"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      opacity: 0.9,
                      display: 'block',
                      mb: 1
                    }}
                  >
                    Unit Test Coverage
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      mb: 1
                    }}
                  >
                    {metrics.coverage !== null ? `${metrics.coverage}%` : 'N/A'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: '1rem',
                      mb: 1
                    }}
                  >
                    Code coverage
                  </Typography>
                  {metrics.repositoryCount > 0 && (
                    <Typography
                      variant="body2"
                      sx={{
                        opacity: 0.8,
                        fontSize: '0.95rem',
                        fontStyle: 'italic'
                      }}
                    >
                      {metrics.repositoryCount} {metrics.repositoryCount === 1 ? 'repository' : 'repositories'}
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    right: -20,
                    top: -20,
                    opacity: 0.2,
                    zIndex: 0
                  }}
                >
                  <CoverageIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* SNYK VULNERABILITIES OUT OF SLA Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            onClick={() => {
              window.location.href = '/?section=security';
            }}
            sx={{
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
              color: 'white',
              boxShadow: 3,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  opacity: 0.9,
                  display: 'block',
                  mb: 1
                }}
              >
                SNYK Vulnerabilities Out of SLA
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  opacity: 0.8,
                  fontSize: '0.8rem',
                  display: 'block',
                  mb: 2
                }}
              >
                As of {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </Typography>
              
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                {/* Critical - Left Aligned */}
                <Box flex={1} textAlign="left">
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: '0.85rem',
                      mb: 0.5
                    }}
                  >
                    Critical
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      mb: 0.5
                    }}
                  >
                    {snykVulnerabilities.critical}
                  </Typography>
                </Box>
                
                {/* High - Right Aligned */}
                <Box flex={1} textAlign="right">
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: '0.85rem',
                      mb: 0.5
                    }}
                  >
                    High
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      mb: 0.5
                    }}
                  >
                    {snykVulnerabilities.high}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  right: -20,
                  top: -20,
                  opacity: 0.2,
                  zIndex: 0
                }}
              >
                <SecurityIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* TOTAL TESTS Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            onClick={() => {
              window.location.href = '/?section=tests';
            }}
            sx={{
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
              color: 'white',
              boxShadow: 3,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  <Typography
                    variant="overline"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      opacity: 0.9,
                      display: 'block',
                      mb: 1
                    }}
                  >
                    Total Tests
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      mb: 1
                    }}
                  >
                    {metrics.totalTests.toLocaleString()}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: '1rem'
                    }}
                  >
                    All tests (auto + manual) Complete suite
                  </Typography>
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    right: -20,
                    top: -20,
                    opacity: 0.2,
                    zIndex: 0
                  }}
                >
                  <TestTubeIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* AUTOMATED TESTS Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            onClick={() => {
              window.location.href = '/?section=tests';
            }}
            sx={{
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
              color: 'white',
              boxShadow: 3,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  <Typography
                    variant="overline"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      opacity: 0.9,
                      display: 'block',
                      mb: 1
                    }}
                  >
                    Automated Tests
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      mb: 1
                    }}
                  >
                    {metrics.automatedTests.toLocaleString()}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: '1rem'
                    }}
                  >
                    UI + API + EPAM
                  </Typography>
                  <Box display="flex" alignItems="center" mt={2}>
                    <TrendingUpIcon sx={{ fontSize: '1.2rem', mr: 0.5, opacity: 0.8 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '1rem' }}>
                      Growing
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    right: -20,
                    top: -20,
                    opacity: 0.2,
                    zIndex: 0
                  }}
                >
                  <TestTubeIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* ACCESSIBILITY WCAG ISSUES Card */}
        <Grid item xs={12} sm={6} md={3}>
            <Card
              onClick={() => {
                window.location.href = '/compliance';
              }}
              sx={{
                height: '100%',
                borderRadius: 2,
                background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                color: 'white',
                boxShadow: 3,
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    opacity: 0.9,
                    display: 'block',
                    mb: 1.5
                  }}
                >
                  Accessibility Issues
                </Typography>
                
                {accessibilityWcag.loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 100 }}>
                    <CircularProgress size={40} sx={{ color: 'white' }} />
                  </Box>
                ) : (
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    {/* WCAG Versions - Compact */}
                    <Box display="flex" flexDirection="column" gap={0.3}>
                      {/* WCAG 2.0 */}
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography
                          variant="body2"
                          sx={{
                            opacity: 0.9,
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}
                        >
                          2.0:
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.9rem'
                          }}
                        >
                          {accessibilityWcag['2.0'].active}/{accessibilityWcag['2.0'].total}
                        </Typography>
                      </Box>
                      
                      {/* WCAG 2.1 */}
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography
                          variant="body2"
                          sx={{
                            opacity: 0.9,
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}
                        >
                          2.1:
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.9rem'
                          }}
                        >
                          {accessibilityWcag['2.1'].active}/{accessibilityWcag['2.1'].total}
                        </Typography>
                      </Box>
                      
                      {/* WCAG 2.2 */}
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography
                          variant="body2"
                          sx={{
                            opacity: 0.9,
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}
                        >
                          2.2:
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.9rem'
                          }}
                        >
                          {accessibilityWcag['2.2'].active}/{accessibilityWcag['2.2'].total}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Divider */}
                    <Box
                      sx={{
                        height: '1px',
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        my: 0.5
                      }}
                    />
                    
                    {/* Overdue Section - Compact */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography
                        variant="body2"
                        sx={{
                          opacity: 0.9,
                          fontSize: '0.75rem',
                          fontWeight: 600
                        }}
                      >
                        Overdue:
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.9rem',
                          color: accessibilityWcag.overdue > 0 ? '#ffeb3b' : 'rgba(255, 255, 255, 0.9)'
                        }}
                      >
                        {accessibilityWcag.overdue}
                      </Typography>
                    </Box>
                    
                    {/* Priority Breakdown - Compact inline */}
                    {Object.keys(accessibilityWcag.byPriority).length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.3 }}>
                        {Object.entries(accessibilityWcag.byPriority)
                          .sort((a, b) => b[1] - a[1]) // Sort by count descending
                          .slice(0, 3) // Show top 3 priorities
                          .map(([priority, count]) => (
                            <Typography key={priority} variant="caption" sx={{ opacity: 0.8, fontSize: '0.65rem' }}>
                              {priority}: {count}
                            </Typography>
                          ))}
                      </Box>
                    )}
                  </Box>
                )}
                
                <Box
                  sx={{
                    position: 'absolute',
                    right: -20,
                    top: -20,
                    opacity: 0.2,
                    zIndex: 0
                  }}
                >
                  <AccessibilityIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

        {/* ENGINEERING PROCESSES Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            onClick={() => {
              window.location.href = '/engineering-processes';
            }}
            sx={{
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              boxShadow: 3,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  opacity: 0.9,
                  display: 'block',
                  mb: 2
                }}
              >
                Engineering Processes
              </Typography>
              
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                {/* RCA - Left Aligned */}
                <Box flex={1} textAlign="left">
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: '0.85rem',
                      mb: 0.5
                    }}
                  >
                    RCA
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      mb: 0.5
                    }}
                  >
                    {engineeringProcesses.loading ? '...' : engineeringProcesses.totalRca}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.8,
                      fontSize: '0.75rem',
                      display: 'block',
                      mb: 1
                    }}
                  >
                    Total
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: engineeringProcesses.overdueRca > 0 ? '#ffeb3b' : 'rgba(255, 255, 255, 0.9)'
                    }}
                  >
                    {engineeringProcesses.loading ? '...' : engineeringProcesses.overdueRca}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.8,
                      fontSize: '0.75rem'
                    }}
                  >
                    Overdue
                  </Typography>
                </Box>
                
                {/* Technical Debt - Right Aligned */}
                <Box flex={1} textAlign="right">
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: '0.85rem',
                      mb: 0.5
                    }}
                  >
                    Tech Debt
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      mb: 0.5
                    }}
                  >
                    {engineeringProcesses.loading ? '...' : engineeringProcesses.totalTechnicalDebt}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.8,
                      fontSize: '0.75rem',
                      display: 'block',
                      mb: 1
                    }}
                  >
                    Total
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: 'rgba(255, 255, 255, 0.9)'
                    }}
                  >
                    {engineeringProcesses.loading ? '...' : engineeringProcesses.approvedTechnicalDebt}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.8,
                      fontSize: '0.75rem'
                    }}
                  >
                    Approved
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  right: -20,
                  top: -20,
                  opacity: 0.2,
                  zIndex: 0
                }}
              >
                <EngineeringIcon sx={{ fontSize: 120, transform: 'rotate(-15deg)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default QAHealthDashboard;

